import React, { useState, useEffect, useCallback } from 'react';
import { reportService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function ProductFrequencyReport() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [sortBy, setSortBy] = useState('least_recent');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartFilters, setChartFilters] = useState({
    fromDate: '',
    toDate: ''
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      const response = await reportService.getProductOrderingFrequency(sortBy);
      if (response.success) {
        setProducts(response.data || []);
      } else {
        setError(response.message || 'Error al cargar el reporte');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar el reporte de frecuencia de pedidos');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchChartData = useCallback(async () => {
    if (!selectedProduct) return;
    
    try {
      setChartLoading(true);
      const response = await reportService.getProductOrderHistory(
        selectedProduct.productId,
        chartFilters.fromDate || null,
        chartFilters.toDate || null
      );
      if (response.success) {
        setChartData(response.data || []);
      }
    } catch (err) {
      console.error('Error loading chart data:', err);
    } finally {
      setChartLoading(false);
    }
  }, [selectedProduct, chartFilters]);

  useEffect(() => {
    if (selectedProduct) {
      fetchChartData();
    }
  }, [selectedProduct, fetchChartData]);

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    setChartFilters({
      fromDate: thirtyDaysAgo.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    });
  };

  const handleChartFilterChange = (field, value) => {
    setChartFilters(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-UY', { timeZone: 'America/Montevideo' });
  };

  const getDaysClass = (days) => {
    if (days < 0) return 'days-never'; // Never ordered
    if (days > 30) return 'days-old';
    if (days > 14) return 'days-warning';
    return 'days-recent';
  };

  const formatDays = (days) => {
    if (days < 0) return 'Nunca';
    return `${days} días`;
  };

  // Simple bar chart using CSS - using reduce for better memory efficiency
  const maxQuantity = chartData.length > 0 
    ? chartData.reduce((max, d) => Math.max(max, d.quantity), 0)
    : 0;

  return (
    <div className="report-container">
      <h2>Reporte de Frecuencia de Pedidos de Productos</h2>
      
      <div className="report-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Ordenar por:</label>
            <div className="button-group">
              <button 
                className={`btn ${sortBy === 'least_recent' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => handleSortChange('least_recent')}
              >
                Menos Recientes
              </button>
              <button 
                className={`btn ${sortBy === 'most_ordered' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => handleSortChange('most_ordered')}
              >
                Más Solicitados
              </button>
            </div>
          </div>
        </div>
        <div className="export-buttons" style={{ marginTop: '10px' }}>
          <button
            onClick={async () => {
              try {
                await reportService.exportProductFrequencyToPdf(sortBy);
              } catch (err) {
                console.error('Error exporting to PDF:', err);
                alert('Error al exportar a PDF');
              }
            }}
            className="btn btn-small btn-primary"
            style={{ marginRight: '10px' }}
          >
            📄 Exportar a PDF
          </button>
          <button
            onClick={async () => {
              try {
                await reportService.exportProductFrequencyToExcel(sortBy);
              } catch (err) {
                console.error('Error exporting to Excel:', err);
                alert('Error al exportar a Excel');
              }
            }}
            className="btn btn-small btn-success"
            style={{ marginRight: '10px' }}
          >
            📊 Exportar a Excel
          </button>
          <button
            onClick={async () => {
              try {
                await reportService.exportProductFrequencyToCsv(sortBy);
              } catch (err) {
                console.error('Error exporting to CSV:', err);
                alert('Error al exportar a CSV');
              }
            }}
            className="btn btn-small btn-info"
          >
            📋 Exportar a CSV
          </button>
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />

      {loading ? (
        <p className="loading-text">Cargando productos...</p>
      ) : (
        <>
          <div className="report-summary">
            <p>
              {sortBy === 'least_recent' 
                ? 'Productos ordenados por tiempo desde el último pedido (más antiguos primero)'
                : 'Productos ordenados por cantidad total solicitada (más solicitados primero)'}
            </p>
          </div>

          {products.length === 0 ? (
            <div className="empty-state">
              <p>No hay datos de pedidos para mostrar</p>
            </div>
          ) : (
            <div className="report-table-container">
              <table className="report-table frequency-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Veces Pedido</th>
                    <th>Cantidad Total</th>
                    <th>Último Pedido</th>
                    <th>Días Sin Pedir</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr 
                      key={product.productId} 
                      className={selectedProduct?.productId === product.productId ? 'selected-row' : ''}
                    >
                      <td>{product.productBarcode}</td>
                      <td>{product.productName}</td>
                      <td>{product.totalOrdersCount}</td>
                      <td><strong>{product.totalQuantityOrdered}</strong></td>
                      <td>{formatDate(product.lastOrderedAt)}</td>
                      <td>
                        <span className={`days-badge ${getDaysClass(product.daysSinceLastOrder)}`}>
                          {formatDays(product.daysSinceLastOrder)}
                        </span>
                      </td>
                      <td>
                        <button 
                          onClick={() => handleProductClick(product)}
                          className="btn btn-small btn-info"
                        >
                          Ver Gráfica
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Chart Section */}
          {selectedProduct && (
            <div className="chart-section">
              <h3>Historial de Pedidos: {selectedProduct.productName}</h3>
              
              <div className="chart-filters">
                <div className="filter-group">
                  <label htmlFor="chartFromDate">Desde</label>
                  <input
                    type="date"
                    id="chartFromDate"
                    value={chartFilters.fromDate}
                    onChange={(e) => handleChartFilterChange('fromDate', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="chartToDate">Hasta</label>
                  <input
                    type="date"
                    id="chartToDate"
                    value={chartFilters.toDate}
                    onChange={(e) => handleChartFilterChange('toDate', e.target.value)}
                  />
                </div>
                <button 
                  onClick={() => setSelectedProduct(null)} 
                  className="btn btn-secondary btn-small"
                >
                  Cerrar
                </button>
              </div>
              
              <div className="export-buttons" style={{ marginTop: '10px', marginBottom: '10px' }}>
                <button
                  onClick={async () => {
                    try {
                      await reportService.exportProductHistoryToPdf(
                        selectedProduct.productId,
                        chartFilters.fromDate || null,
                        chartFilters.toDate || null
                      );
                    } catch (err) {
                      console.error('Error exporting to PDF:', err);
                      alert('Error al exportar a PDF');
                    }
                  }}
                  className="btn btn-small btn-primary"
                  style={{ marginRight: '10px' }}
                >
                  📄 Exportar Historial a PDF
                </button>
                <button
                  onClick={async () => {
                    try {
                      await reportService.exportProductHistoryToExcel(
                        selectedProduct.productId,
                        chartFilters.fromDate || null,
                        chartFilters.toDate || null
                      );
                    } catch (err) {
                      console.error('Error exporting to Excel:', err);
                      alert('Error al exportar a Excel');
                    }
                  }}
                  className="btn btn-small btn-success"
                  style={{ marginRight: '10px' }}
                >
                  📊 Exportar Historial a Excel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await reportService.exportProductHistoryToCsv(
                        selectedProduct.productId,
                        chartFilters.fromDate || null,
                        chartFilters.toDate || null
                      );
                    } catch (err) {
                      console.error('Error exporting to CSV:', err);
                      alert('Error al exportar a CSV');
                    }
                  }}
                  className="btn btn-small btn-info"
                >
                  📋 Exportar Historial a CSV
                </button>
              </div>

              {chartLoading ? (
                <p className="loading-text">Cargando gráfica...</p>
              ) : chartData.length === 0 ? (
                <div className="empty-state">
                  <p>No hay datos de pedidos para el período seleccionado</p>
                </div>
              ) : (
                <div className="simple-chart">
                  <div className="chart-container">
                    {chartData.map((data, index) => (
                      <div key={index} className="chart-bar-container">
                        <div 
                          className="chart-bar"
                          style={{ 
                            height: `${(data.quantity / maxQuantity) * 100}%`,
                            minHeight: '10px'
                          }}
                        >
                          <span className="bar-value">{data.quantity}</span>
                        </div>
                        <span className="bar-label">{formatDate(data.date)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="chart-summary">
                    <p>
                      <strong>Total en período:</strong> {chartData.reduce((sum, d) => sum + d.quantity, 0)} unidades
                    </p>
                    <p>
                      <strong>Promedio por día:</strong> {(chartData.reduce((sum, d) => sum + d.quantity, 0) / chartData.length).toFixed(1)} unidades
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ProductFrequencyReport;
